// Haven Brand Colors - From Figma Brand Guidelines
// https://www.figma.com/design/1nqDpQX9AnV6HBpx6L8gjH/Haven-Brand-Guidelines

// @claude: clean this up so that all of the colors are in one map. then reference the variable where we have the colors hardcoded.

/**
 * Primary Brand Colors
 */
export const brandColors = {
  slate: "#1A1F2B",

  // Primary Green Palette
  mint: "#A3E7AE",
  mintLight: "#C4FCCD", // Mint-Light (ENG-2092)
  algaeGreen: "#7ADCA3",
  forest: "#042B1C",

  // Cyan/Teal Palette
  elm: "#1A7A82",
  eden: "#0F5257",

  // Purple/Magenta Palette
  plum: "#401F3E", // official "Plum" — also the brand "Text-Purple"
  eggplant: "#6B4869",
  amethystSmoke: "#A583AE", // official "Spring" (kept the existing name — same value, no duplicate)

  // Blue Palette
  waterloo: "#817E9F",
  sky: "#427FB4", // Sky (ENG-2092)
  skyDark: "#245977", // Sky-Dark (ENG-2092)

  // Neutral Palette
  prim: "#F7F0F5",
  grayGreen: "#71967C", // gray-green (ENG-2092)

  // Orange Palette
  tamarind: "#3A2017", // official "Nest" (kept the existing name — same value, no duplicate)

  // The one brand red — danger only. The brand positions AGAINST "alarm reds", so this is used
  // sparingly for genuine danger (ENG-2092; replaces the placeholder plum-error mapping).
  red: "#A40000",
} as const;
// NB: the brand "Text-Black" #191918 already lives in neutralColors.jaguar (used for primary text).

/**
 * SANCTIONED NON-BRAND EXCEPTION — amber caution.
 *
 * The Haven brand has NO amber and positions against alarm reds, but Colin granted explicit
 * permission (ENG-2092) to keep an amber for genuine alert/caution needs. This is deliberately
 * NOT a brand token — it is the single canonical source for the caution amber so a second amber
 * hex can't drift in. Consumed by the caution email banner AND the extension pill's caution tier
 * (via content-styles.ts, which references these). Values are locked: #D97706 / #92400E / #FDF0E1.
 */
export const cautionColors = {
  caution: "#D97706", // fill / dot
  cautionDark: "#92400E", // text / border
  cautionBg: "#FDF0E1", // surface tint
} as const;

/**
 * SANCTIONED NON-BRAND EXCEPTION — verified pill fill.
 *
 * The 'Verified' (non-shielded) pill needs a medium green with enough depth for a legible
 * dark-green mark/text. No official brand green works: Algae/Mint/Mint-Light are light pastels
 * that fail contrast as a fill; Eden is already the protected tone and Elm the ambient/unknown
 * tone — borrowing either collapses a tier distinction. Deliberately NOT a brand token; single
 * canonical source so a second verified-green can't drift in. (ENG-2384; decision: sanction.)
 */
export const verifiedColors = {
  verifiedFill: "#1a9d6e", // fill; label/mark = brandColors.forest #042B1C (WCAG-passing)
} as const;

/**
 * Neutral Colors
 */
export const neutralColors = {
  // Whites
  white: "#FFFFFF",
  neutralWhite: "#FBFEFF",

  // Blacks
  black: "#000000",
  neutralBlack: "#060606",
  b: "#070707",

  // Grays (from darkest to lightest)
  codGray: "#1A1A1A", // grey/10
  jaguar: "#191918", // Almost black, used for primary text
  mineShaft: "#333333", // grey/20
  tundora: "#444444", // grey/27
  doveGray: "#666666", // grey/40
  dustyGray: "#999999", // grey/60
  silver: "#CCCCCC", // grey/80
  concrete: "#E0E0E0", // grey/88
  gallery: "#E5E5E5", // grey/90
  wildSand: "#F9F9F9", // grey/97
  prim: "#F7F0F5", // grey/95
  alabaster: "#FAFAFA", // grey/98
  athensGray: "#E8E8E8", // Light border gray
} as const;

/**
 * Semantic Color Palette
 * Maps brand colors to semantic usage
 */
export const colors = {
  // Primary - Using green palette as primary
  primary: brandColors.algaeGreen,
  primaryLight: brandColors.mint,
  primaryDark: brandColors.forest,

  // Secondary - Using cyan/teal palette
  secondary: brandColors.elm,
  secondaryLight: brandColors.elm,
  secondaryDark: brandColors.eden,

  // Accent - Using purple palette
  accent: brandColors.waterloo,
  accentLight: brandColors.amethystSmoke,
  accentDark: brandColors.plum,

  // Success - Using green palette
  success: brandColors.algaeGreen,
  successLight: brandColors.mint,
  successDark: brandColors.forest,

  // Warning - the sanctioned amber caution exception (ENG-2092; was the dark-brown tamarind).
  warning: cautionColors.caution,
  warningLight: cautionColors.cautionBg,
  warningDark: cautionColors.cautionDark,

  // Error - the one brand red (ENG-2092; corrected from the placeholder plum. There is no brand
  // "light red", so all three map to the single red — apply opacity at the call site for tints).
  error: brandColors.red,
  errorLight: brandColors.red,
  errorDark: brandColors.red,

  // Text colors
  text: {
    primary: neutralColors.jaguar,
    secondary: neutralColors.doveGray,
    light: neutralColors.dustyGray,
    lighter: neutralColors.silver,
    inverse: neutralColors.white,
    muted: neutralColors.tundora,
  },

  // Background colors
  background: {
    primary: neutralColors.white,
    secondary: neutralColors.prim,
    tertiary: neutralColors.alabaster,
    light: neutralColors.wildSand,
    dark: neutralColors.codGray,
    darkAlt: brandColors.slate,
  },

  // Border colors
  border: {
    lightest: neutralColors.athensGray,
    light: neutralColors.gallery,
    medium: neutralColors.concrete,
    dark: neutralColors.silver,
  },

  // Gradients
  gradients: {
    greenButton: "linear-gradient(135deg, #7ADCA3 0%, #A3E7AE 100%)",
    cash: "linear-gradient(135deg, #7ADCA3 0%, #A3E7AE 100%)",
    primary: "linear-gradient(135deg, #7ADCA3 0%, #A3E7AE 100%)",
    cyan: "linear-gradient(135deg, #0F5257 0%, #1A7A82 100%)",
    purple: "linear-gradient(135deg, #401F3E 0%, #6B4869 100%)",
    heroText: "linear-gradient(135deg, #7ADCA3 0%, #1A7A82 100%)",
    tealHero: "linear-gradient(180deg, #1E7A73 0%, #1A6B65 100%)",
  },

  // Color variations with opacity
  alpha: {
    white20: "#ffffff33",
    white90: "#ffffffe6",
    algaeGreen10: "#7adca31a",
    algaeGreen15: "#7adca326",
    waterloo10: "#817e9f1a",
    elm15: "#1a7a8226",
  },

  // Shadow colors
  shadows: {
    small: "rgba(0, 0, 0, 0.05)",
    medium: "rgba(0, 0, 0, 0.1)",
    large: "rgba(0, 0, 0, 0.15)",
    primary: "rgba(122, 220, 163, 0.15)",
    secondary: "rgba(26, 122, 130, 0.15)",
    accent: "rgba(129, 126, 159, 0.15)",
  },
} as const;

/**
 * Semantic color aliases for component usage
 */
export const semanticColors = {
  // Links
  link: colors.secondary,
  linkHover: colors.secondaryDark,

  // Buttons
  buttonPrimary: colors.primary,
  buttonPrimaryHover: colors.primaryDark,
  buttonSecondary: colors.secondary,
  buttonSecondaryHover: colors.secondaryDark,

  // Navigation
  navbarBg: colors.background.primary,
  navbarBorder: colors.border.light,

  // Cards
  cardBg: colors.background.primary,
  cardBorder: colors.border.light,
  cardBorderHover: colors.border.medium,

  // Accents
  heroAccent: colors.primary,
  featureAccent: colors.accent,

  // Alert semantics (ENG-2092) — caution is the sanctioned amber exception; danger is the one brand red.
  caution: cautionColors.caution,
  cautionDark: cautionColors.cautionDark,
  cautionBg: cautionColors.cautionBg,
  warning: cautionColors.caution,
  danger: brandColors.red,

  slate: "#1A1F2B",
} as const;

/**
 * Button tokens (ENG-2377 / ENG-2378) — the canonical two-tier button system. Every package consumes
 * these instead of hardcoding button hexes, and the two hover values that apps used to invent ad hoc
 * (`#6FBA9A` gradient ends, per-app `#0a3d41`) are decided ONCE here:
 *
 *  - **marketing** — the public marketing CTA: the bright algae→mint gradient with Forest text. Its
 *    hover collapses the gradient to its deeper brand stop (flat Algae Green) — no off-brand `#6FBA9A`.
 *    `fill` is the flat (non-gradient) marketing fill for smaller/rect CTAs.
 *  - **app** — the product/app-tier CTA (account / admin / landing / extension): solid Eden with white
 *    text. `backgroundHover` is the single canonical dark-Eden hover `#0A3D41` (replaces the ad-hoc
 *    per-app copies of that hex).
 *  - **danger / caution** — the two alert-action fills (the one brand Red; the sanctioned amber).
 */
export const buttonTokens = {
  marketing: {
    background: colors.gradients.greenButton, // linear-gradient(135deg, #7ADCA3 0%, #A3E7AE 100%)
    backgroundHover: brandColors.algaeGreen,  // #7ADCA3 — the gradient's deeper stop, flat, on hover
    fill: brandColors.mint,                   // #A3E7AE — flat marketing fill (non-gradient buttons)
    text: brandColors.forest,                 // #042B1C
  },
  app: {
    background: brandColors.eden,             // #0F5257
    backgroundHover: "#0A3D41",               // canonical dark-Eden hover (was ad hoc across apps)
    text: neutralColors.white,                // #FFFFFF
  },
  danger: {
    background: brandColors.red,              // #A40000 — the one brand Red
    text: neutralColors.white,
  },
  caution: {
    background: cautionColors.caution,        // #D97706 — the sanctioned amber caution
    text: neutralColors.white,
  },
} as const;

/**
 * Type definitions
 */
export type BrandColor = keyof typeof brandColors;
export type NeutralColor = keyof typeof neutralColors;
export type ColorKeys = keyof typeof colors;
export type SemanticColorKeys = keyof typeof semanticColors;
export type ButtonToken = keyof typeof buttonTokens;

/**
 * A brand colour at a given opacity, derived from the token rather than restated (ENG-2533).
 *
 * The console had 68 hand-written `rgba(4, 43, 28, 0.06)`-style literals across 18 files. Each was
 * correct on the day it was typed and silently independent of the palette from then on: change
 * `brandColors.forest` and every one of them keeps rendering the old colour, with nothing to catch
 * it — they are not wrong, so no test fails and no reviewer sees a conflict.
 *
 * Takes a hex token and returns the `rgba()` string, so the alpha stays visible at the call site
 * (which is the part a designer changes) while the hue comes from one place.
 *
 * Accepts only 6-digit hex, which is what every `brandColors` entry is. Anything else throws rather
 * than silently emitting `rgba(NaN, NaN, NaN, …)`, which CSS ignores — a wrong colour is visible in
 * review, an ignored declaration is not.
 */
export function withAlpha(hex: string, alpha: number): string {
  const match = /^#([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) {
    throw new Error(`withAlpha expects a 6-digit hex colour, got "${hex}"`);
  }
  const int = parseInt(match[1], 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
